import type {
  DefaultTheme,
  FlattenSimpleInterpolation,
  StyledComponent,
} from 'styled-components';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import type { ITheme } from '../../utils/theme';
import Select from '../deprecated-select/select';
import Icon from '../icon/icon';
import Typography from '../typography/typography';
import { getCounterColor } from './helper-functions/colors';
import {
  getCurrencySelectBorder,
  getPaddingLeft,
} from './helper-functions/currency';

interface IStyledInput {
  theme: ITheme;
  isdirty: number;
  width?: string;
  $hasError: number;
  height?: string;
  $currency?: string;
  $showCurrencySelect: boolean;
}

const inputVariants = ({
  theme,
  height,
  $currency,
  $showCurrencySelect,
}: IStyledInput) =>
  variant({
    variants: {
      text: {
        padding: theme.spacing(1.5),
      },
      textarea: {
        height: height ?? theme.input.textarea.height,
        overflow: 'auto',
        padding: '8px 12px',
        resize: 'none',
      },
      amount: {
        height: height ?? theme.input.text.height,
        padding: '10px 12px',
        paddingLeft: getPaddingLeft($currency, $showCurrencySelect),
        boxSizing: 'border-box',
      },
      icon: {
        paddingLeft: theme.spacing(6),
      },
    },
  });

const baseDesign: ({
  theme,
  width,
  $hasError,
  height,
}: IStyledInput) => FlattenSimpleInterpolation = ({
  theme,
  width,
  $hasError,
  height,
}: IStyledInput): FlattenSimpleInterpolation => {
  return css`
    box-sizing: border-box;
    position: relative;
    width: 100%;
    max-width: ${width ?? theme.input.text.width};
    height: ${height ?? theme.input.text.height};
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.palette.neutral['00']};
    line-height: 22px;
    color: ${$hasError
      ? theme.palette.error.main
      : theme.palette.neutral['100']};
    border: ${$hasError
      ? `0.5px solid ${theme.palette.error.main}`
      : `0.5px solid ${theme.palette.neutral[90]}`};

    &:focus-visible {
      box-sizing: border-box;
      outline: 2px solid
        ${$hasError ? theme.palette.error.main : theme.palette.neutral['100']};
      color: ${$hasError
        ? theme.palette.error.main
        : theme.palette.neutral['100']};
    }

    &::placeholder {
      color: ${theme.palette.neutral['80']};
    }

    &:disabled {
      background: ${theme.palette.neutral['50']};
      color: ${theme.palette.neutral['70']};
      border: 0.5px solid ${theme.palette.neutral['70']};
    }

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      max-width: 100%;
      // font size of 16px on mobile to avoid zoom
      font-size: 16px;
    }
  `;
};

const StyledTypographyInput = styled('input')(
  ({ theme }) => theme.typography.bodyMd1
);

export const StyledInput: StyledComponent<
  'input',
  DefaultTheme,
  Record<string, unknown> & IStyledInput,
  never
> = styled(StyledTypographyInput)(baseDesign, inputVariants);

export const InputWrapper = styled.div`
  ${({ theme, width }: { theme: ITheme; width?: string }) => css`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: initial;
    justify-content: flex-start;
    width: ${width ?? '100%'};
    max-width: ${width ?? theme.input.text.width};
    font-family: ${theme.fontFamily};
    position: relative;

    @media (max-width: ${theme.breakpoints.sm}) {
      max-width: 100%;
    }
  `}
`;

export const StyledIcon = styled(Icon)`
  ${({ theme }) => css`
    box-sizing: border-box;
    padding-top: 24px;
    z-index: 1;
    box-shadow: none;
    margin-left: ${theme.spacing(2)};
  `};
`;

export const IconWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 30px;
  max-width: 30px;
  position: absolute;
`;

export const CurrencySelect = styled(Select)`
  ${({
    theme,
    $hasError,
    $symbol,
  }: {
    theme: ITheme;
    $hasError: boolean;
    $symbol?: string;
  }) => css`
    box-sizing: border-box;
    max-width: 105px;
    margin-bottom: ${theme.spacing(-6)};
    z-index: 1;
    box-shadow: none;
    & > span {
      margin-left: ${theme.spacing(2)};
    }
    ${getCurrencySelectBorder($hasError, theme)};

    :after {
      content: '${$symbol}';
      position: absolute;
      left: calc(100% + 10px);
      font-weight: 400;
      pointer-events: none; // Click through to input rather than open dropdown
    }
  `};
`;

export const Counter = styled(Typography)`
  ${({
    theme,
    $hasError,
    $isDisabled,
  }: {
    theme: ITheme;
    $hasError: boolean;
    $isDisabled: boolean;
  }) => css`
    width: fit-content;
    color: ${getCounterColor($hasError, $isDisabled, theme)};
    right: 4px;
    bottom: ${$hasError ? '25px' : '4px'};
    z-index: 1;
    position: absolute;
  `}
`;

export const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(1)};
  `}
`;

export const ClearWrapper = styled.div`
  position: absolute;
  top: 28px;
  right: 0;
`;

export const PrefixSymbol = styled.div`
  ${({ theme }) => css`
    position: absolute;
  `}
`;

export const TopRightSection = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

export const RightSection = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 22px;
    right: 10px;
    height: ${theme.input.text.height};
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;
